import React, { useEffect, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Download, Nfc, QrCode, Check, Loader2, Layers } from "lucide-react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

export default function QRStudio() {
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState("");
  const [writing, setWriting] = useState(false);
  const [written, setWritten] = useState(false);
  const [batch, setBatch] = useState(false);
  const [batchCount, setBatchCount] = useState(0);
  const [batchLog, setBatchLog] = useState([]);
  const batchRef = useRef(false);
  const countRef = useRef(0);
  const qrRef = useRef();

  useEffect(() => { api.get("/cards").then((r) => { setCards(r.data); if (r.data[0]) setSelected(r.data[0].slug); }); }, []);

  const url = selected ? `${window.location.origin}/c/${selected}` : "";

  const downloadQR = () => {
    const canvas = qrRef.current.querySelector("canvas");
    const link = document.createElement("a");
    link.download = `iwurks360-qr-${selected}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded");
  };

  const writeNFC = async () => {
    setWritten(false);
    setWriting(true);
    const onSuccess = (msg) => {
      setWriting(false); setWritten(true);
      if (batchRef.current) {
        const cardName = cards.find((c) => c.slug === selected)?.full_name || selected;
        const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        countRef.current += 1;
        const idx = countRef.current;
        setBatchCount(idx);
        setBatchLog((l) => [...l, { id: `${idx}-${time}`, index: idx, name: cardName, time }]);
        toast.success(`Tag #${idx} written — present the next tag`);
        setTimeout(() => { if (batchRef.current) { setWritten(false); writeNFC(); } }, 1300);
      } else {
        toast.success(msg);
      }
    };
    // Attempt real Web NFC where available; otherwise simulate.
    if ("NDEFReader" in window) {
      try {
        const ndef = new window.NDEFReader();
        await ndef.write({ records: [{ recordType: "url", data: url }] });
        onSuccess("NFC tag written — tap to test!");
        return;
      } catch (e) { /* fall back to simulation */ }
    }
    setTimeout(() => onSuccess("NFC tag programmed (simulated)"), 1600);
  };

  const stopBatch = () => {
    batchRef.current = false;
    setBatch(false); setWriting(false); setWritten(false);
    const n = countRef.current;
    toast.success(`Batch complete — ${n} tag${n === 1 ? "" : "s"} programmed`);
  };
  const startBatch = () => {
    setBatchCount(0); setBatchLog([]); setWritten(false); countRef.current = 0;
    batchRef.current = true; setBatch(true);
    setTimeout(() => writeNFC(), 50);
  };

  return (
    <DashboardLayout title="QR & NFC Studio">
      {cards.length === 0 ? (
        <div className="iw-card p-12 text-center"><p className="text-[#8B9BB4]">Create a card first to generate QR codes and NFC tags.</p></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 fade-up">
          <div className="iw-card p-6">
            <label className="text-xs text-[#8B9BB4] font-semibold mb-1.5 block">Select card</label>
            <select className="iw-input" value={selected} onChange={(e) => { setSelected(e.target.value); setWritten(false); batchRef.current = false; setBatch(false); }} data-testid="qr-card-select">
              {cards.map((c) => <option key={c.id} value={c.slug}>{c.full_name || c.slug}</option>)}
            </select>
            <p className="text-xs text-[#8B9BB4] mt-3 break-all">{url}</p>
          </div>

          <div className="iw-card p-8 flex flex-col items-center">
            <p className="overline mb-4 flex items-center gap-2"><QrCode size={14} /> QR Code</p>
            <div ref={qrRef} className="bg-white p-4 rounded-xl">
              {url && <QRCodeCanvas value={url} size={200} level="H" fgColor="#081426" data-testid="qr-canvas" />}
            </div>
            <button onClick={downloadQR} className="btn-gold px-6 py-3 rounded-full mt-6 flex items-center gap-2" data-testid="download-qr-btn"><Download size={16} /> Download PNG</button>
          </div>

          <div className="iw-card p-8 lg:col-span-2 flex flex-col md:flex-row items-center gap-8">
            <div className="relative shrink-0 flex items-center justify-center h-40 w-56" data-testid="nfc-stage">
              {/* radiating pulse rings while writing */}
              {writing && (
                <>
                  <span className="absolute h-24 w-24 rounded-full border-2 border-[#D4AF37]/60 nfc-ping" style={{ animationDelay: "0s" }} />
                  <span className="absolute h-24 w-24 rounded-full border-2 border-[#D4AF37]/40 nfc-ping" style={{ animationDelay: "0.5s" }} />
                  <span className="absolute h-24 w-24 rounded-full border-2 border-[#D4AF37]/20 nfc-ping" style={{ animationDelay: "1s" }} />
                </>
              )}
              {/* the NFC tag */}
              <div className={`h-28 w-44 rounded-xl border flex items-center justify-center relative overflow-hidden transition-all duration-500 ${written ? "border-green-500/60" : "border-[#D4AF37]/40"} ${writing ? "scale-95" : "scale-100"}`}
                style={{ background: written ? "linear-gradient(120deg,#0d2f1e,#081426)" : "linear-gradient(120deg,#142B5A,#081426)" }}>
                {written
                  ? <div className="flex flex-col items-center gap-1 fade-up"><Check size={34} className="text-green-400" strokeWidth={2} /><span className="text-xs text-green-400 font-semibold">Tag ready</span></div>
                  : <Nfc size={40} className={`text-[#D4AF37] ${writing ? "animate-pulse" : ""}`} strokeWidth={1.5} />}
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="overline">NFC Smart Card Studio</p>
              <h3 className="font-display text-2xl font-bold mt-1">Tap to program a physical NFC tag</h3>
              <p className="text-sm text-[#8B9BB4] mt-2">
                {writing ? (batch ? `Batch mode — present tag #${batchCount + 1} to your device…` : "Hold an NFC card or badge to the back of your device…")
                  : written ? "Success! Your NFC tag now opens this digital card when tapped. Grab another tag or reprogram anytime."
                  : "Write your digital card URL to any NFC card or badge. On supported devices (Android Chrome) this writes a real tag; otherwise it runs a secure simulation."}
              </p>
              {batch && <p className="text-sm font-semibold text-[#D4AF37] mt-2" data-testid="nfc-batch-count">{batchCount} tag{batchCount === 1 ? "" : "s"} programmed this batch</p>}
              <div className="flex flex-wrap gap-2 mt-4 items-center justify-center md:justify-start">
                {!batch ? (
                  <>
                    <button onClick={writeNFC} disabled={writing} className="btn-gold px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-70" data-testid="write-nfc-btn">
                      {writing ? <><Loader2 size={16} className="animate-spin" /> Writing…</> : <><Nfc size={16} /> {written ? "Write Another Tag" : "Write to NFC Tag"}</>}
                    </button>
                    <button onClick={startBatch} disabled={writing} className="btn-ghost px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="start-batch-btn">
                      <Layers size={16} /> Program Many Tags
                    </button>
                  </>
                ) : (
                  <button onClick={stopBatch} className="btn-gold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="stop-batch-btn">
                    <Check size={16} /> Done — Stop Batch
                  </button>
                )}
              </div>
            </div>
          </div>

          {!batch && batchLog.length > 0 && (
            <div className="iw-card p-6 lg:col-span-2" data-testid="nfc-batch-summary">
              <div className="flex items-center justify-between mb-4">
                <p className="overline flex items-center gap-2"><Layers size={14} /> Last batch — {batchLog.length} tag{batchLog.length === 1 ? "" : "s"} written</p>
                <button onClick={() => setBatchLog([])} className="text-xs text-[#8B9BB4] hover:text-red-400 transition-colors" data-testid="clear-batch-summary">Clear</button>
              </div>
              <div className="space-y-2">
                {batchLog.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 text-sm border-b border-[#ffffff1a]/50 pb-2" data-testid={`batch-log-${e.index}`}>
                    <span className="h-6 w-6 rounded-full bg-green-500/15 text-green-400 flex items-center justify-center text-xs font-bold shrink-0">{e.index}</span>
                    <span className="text-[#F6F1E4] flex-1 truncate">{e.name}</span>
                    <span className="text-xs text-[#8B9BB4] shrink-0">{e.time}</span>
                    <Check size={14} className="text-green-400 shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
